import * as React from 'react'
import { ReactElement, useMemo, useState } from 'react'
import { useFormikContext } from 'formik'
import { FamilyLawModuleDto, getDefaultRequest, RequestDto } from '../../../../../hooks/module/useFamilyLaw'
import {
    Button,
    Dialog,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogSurface,
    DialogTitle,
    DialogTrigger,
} from '@fluentui/react-components'
import { CheckmarkRegular, DismissRegular } from '@fluentui/react-icons'
import Body from './Body'
import * as Yup from 'yup'
import { ValidationError } from 'yup'
import Form from '../../../Form'
import { FamilyLawDialogAction, FamilyLawDialogProps } from '../../../../../pages/File/Registration/FamilyLawOverview'
import { RequestType } from '../Requests'

const validationSchema = Yup.object().shape({
    type: Yup.string().required('Proceduretype is verplicht'),
    provisionalContent: Yup.array().when('type', {
        is: (value: RequestType) => value === RequestType.Provisional,
        then: schema => schema.min(1, 'Verzoek bevat geen inhoud'),
        otherwise: schema => schema.notRequired(),
    }),
    meritContent: Yup.array().when('type', {
        is: (value: RequestType) => value !== RequestType.Provisional,
        then: schema => schema.min(1, 'Verzoek bevat geen inhoud'),
        otherwise: schema => schema.notRequired(),
    }),
    referenceDate: Yup.string()
        .nullable()
        .test('valid-date', 'Peildatum mag niet later zijn vandaag', value =>
            !!value?.trim() ? new Date() > new Date(value) : true,
        ),
    decreeDate: Yup.string().when('referenceDate', {
        is: value => !value || value.trim() === '',
        then: schema => schema.notRequired(),
        otherwise: schema =>
            schema.test('valid-date', 'Beschikkingsdatum mag niet eerder zijn dan de peildatum', (value, ctx) =>
                !!value?.trim() ? new Date(ctx.parent.referenceDate) <= new Date(value) : true,
            ),
    }),
    decreeRegisterDate: Yup.string().when('decreeDate', {
        is: (value: string) => !value || value.trim() === '',
        then: schema => schema.notRequired(),
        otherwise: schema =>
            schema.test(
                'valid-date',
                'Inschrijvingsdatum mag niet eerder zijn dan de beschikkingsdatum',
                (value, ctx) => (!!value?.trim() ? new Date(ctx.parent.decreeDate) <= new Date(value) : true),
            ),
    }),
})

export default function RequestDialog(props: FamilyLawDialogProps): ReactElement {
    const { values } = useFormikContext<FamilyLawModuleDto>()
    const baseRequest = useMemo(() => {
        return props.action === FamilyLawDialogAction.Add ? getDefaultRequest(values.id) : values.requests[props.index]
    }, [props.open])
    const [request, setRequest] = useState<RequestDto>(baseRequest)
    const [validationError, setValidationError] = useState([])

    return (
        <Dialog
            open={props.open}
            onOpenChange={(_, data) => {
                props.setOpen(data.open)
            }}
        >
            <DialogSurface>
                <Form globalErrors={validationError}>
                    <DialogBody>
                        <DialogTitle>{`Procedure ${
                            props.action === FamilyLawDialogAction.Add ? 'toevoegen' : 'aanpassen'
                        }`}</DialogTitle>
                        <DialogContent>
                            <Body request={request} setRequest={setRequest} {...props} />
                        </DialogContent>
                        <DialogActions>
                            <DialogTrigger disableButtonEnhancement>
                                <Button appearance="transparent" icon={<DismissRegular />}>
                                    Annuleren
                                </Button>
                            </DialogTrigger>
                            <Button
                                appearance="primary"
                                icon={<CheckmarkRegular />}
                                onClick={() => {
                                    validationSchema.validate(request, { abortEarly: false }).then(
                                        () => {
                                            props.action === FamilyLawDialogAction.Add
                                                ? props.push(request)
                                                : props.replace(props.index, request)
                                            props.setOpen(false)
                                        },
                                        (error: ValidationError) => {
                                            setValidationError(error.inner)
                                        },
                                    )
                                }}
                            >
                                Opslaan
                            </Button>
                        </DialogActions>
                    </DialogBody>
                </Form>
            </DialogSurface>
        </Dialog>
    )
}
